import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  FileText,
  Upload,
  Search,
  Trash2,
  Eye,
  FileQuestion,
  GitCompare,
  List,
  Loader2,
  Download,
  Clock,
  FileType,
  ChevronDown,
  ChevronUp,
  X,
  Sparkles,
  BookOpen,
  MessageSquare,
  LayoutList
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const DocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  
  // Selected documents for comparison
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);
  
  // Analysis modal
  const [analysisModal, setAnalysisModal] = useState(false);
  const [currentDoc, setCurrentDoc] = useState(null);
  const [analysisType, setAnalysisType] = useState("summary");
  const [analysisLength, setAnalysisLength] = useState("medium");
  const [analysisQuestion, setAnalysisQuestion] = useState("");
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // View modal
  const [viewModal, setViewModal] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  
  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Erreur lors du chargement des documents");
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);
  
  // Upload document
  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const formData = new FormData();
    
    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }
    
    try {
      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(`${response.data.uploaded.length} document(s) uploadé(s)`);
      fetchDocuments();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  
  // Delete document
  const handleDelete = async (docId) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    
    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success("Document supprimé");
      fetchDocuments();
      setSelectedDocs(prev => prev.filter(id => id !== docId));
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };
  
  // Analyze document
  const openAnalysisModal = (doc) => {
    setCurrentDoc(doc);
    setAnalysisResult(null);
    setAnalysisQuestion("");
    setAnalysisModal(true);
  };
  
  const runAnalysis = async () => {
    if (!currentDoc) return;
    if (analysisType === "question" && !analysisQuestion.trim()) {
      toast.error("Veuillez poser une question");
      return;
    }
    
    setAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      const response = await axios.post(`${API}/documents/${currentDoc.id}/analyze`, {
        analysis_type: analysisType,
        target_length: analysisLength,
        question: analysisType === "question" ? analysisQuestion : null
      });
      
      setAnalysisResult(response.data.result);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur d'analyse");
    } finally {
      setAnalyzing(false);
    }
  };
  
  // Compare documents
  const toggleDocSelection = (docId) => {
    setSelectedDocs(prev => {
      if (prev.includes(docId)) {
        return prev.filter(id => id !== docId);
      }
      if (prev.length >= 5) {
        toast.error("Maximum 5 documents pour la comparaison");
        return prev;
      }
      return [...prev, docId];
    });
  };
  
  const runComparison = async () => {
    if (selectedDocs.length < 2) {
      toast.error("Sélectionnez au moins 2 documents");
      return;
    }
    
    setComparing(true);
    setCompareResult(null);
    
    try {
      const response = await axios.post(`${API}/documents/compare`, {
        document_ids: selectedDocs
      });
      
      setCompareResult(response.data.result);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de comparaison");
    } finally {
      setComparing(false);
    }
  };
  
  // Search in documents
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setSearchResults(null);
    
    try {
      const response = await axios.post(`${API}/documents/search`, {
        query: searchQuery
      });
      
      setSearchResults(response.data.results);
    } catch (error) {
      toast.error("Erreur de recherche");
    } finally {
      setSearching(false);
    }
  };
  
  // View document
  const openViewModal = (doc) => {
    setViewDoc(doc);
    setViewModal(true);
  };
  
  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  // Format date
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };
  
  // Get file type icon/color
  const getFileTypeStyle = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();
    switch (ext) {
      case "pdf":
        return { color: "text-red-500", bg: "bg-red-100" };
      case "docx":
      case "doc":
        return { color: "text-blue-500", bg: "bg-blue-100" };
      case "txt":
        return { color: "text-gray-500", bg: "bg-gray-100" };
      default:
        return { color: "text-purple-500", bg: "bg-purple-100" };
    }
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Base Documentaire</h2>
          <p className="text-sm text-muted-foreground">
            Uploadez, analysez et comparez vos documents
          </p>
        </div>
        
        <div className="flex gap-2">
          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              accept=".pdf,.docx,.txt"
              onChange={handleUpload}
              className="hidden"
              data-testid="document-upload-input"
            />
            <Button disabled={uploading} asChild>
              <span>
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Ajouter des documents
              </span>
            </Button>
          </label>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{documents.length}</p>
                <p className="text-xs text-muted-foreground">Documents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <FileType className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {documents.filter(d => d.filename.endsWith('.pdf')).length}
                </p>
                <p className="text-xs text-muted-foreground">PDF</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileType className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {documents.filter(d => d.filename.endsWith('.docx')).length}
                </p>
                <p className="text-xs text-muted-foreground">Word</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {documents.reduce((acc, d) => acc + (d.stats?.words || 0), 0).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">Mots totaux</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Main Content */}
      <Tabs defaultValue="documents" className="space-y-4">
        <TabsList>
          <TabsTrigger value="documents" data-testid="tab-documents">
            <LayoutList className="w-4 h-4 mr-2" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="search" data-testid="tab-search">
            <Search className="w-4 h-4 mr-2" />
            Recherche
          </TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare">
            <GitCompare className="w-4 h-4 mr-2" />
            Comparer ({selectedDocs.length})
          </TabsTrigger>
        </TabsList>
        
        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : documents.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Aucun document</h3>
                <p className="text-sm text-muted-foreground text-center mb-4">
                  Uploadez vos premiers documents pour commencer l&apos;analyse
                </p>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.docx,.txt"
                    onChange={handleUpload}
                    className="hidden"
                  />
                  <Button asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      Ajouter des documents
                    </span>
                  </Button>
                </label>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {documents.map((doc) => {
                const typeStyle = getFileTypeStyle(doc.filename);
                const isSelected = selectedDocs.includes(doc.id);
                
                return (
                  <Card 
                    key={doc.id} 
                    className={`transition-all ${isSelected ? 'ring-2 ring-primary' : ''}`}
                    data-testid={`document-card-${doc.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Checkbox for comparison */}
                        <button
                          onClick={() => toggleDocSelection(doc.id)}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors ${
                            isSelected 
                              ? 'bg-primary border-primary text-white' 
                              : 'border-gray-300 hover:border-primary'
                          }`}
                          data-testid={`select-doc-${doc.id}`}
                        >
                          {isSelected && <span className="text-xs">✓</span>}
                        </button>
                        
                        {/* File icon */}
                        <div className={`w-12 h-12 rounded-lg ${typeStyle.bg} flex items-center justify-center flex-shrink-0`}>
                          <FileText className={`w-6 h-6 ${typeStyle.color}`} />
                        </div>
                        
                        {/* Document info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium truncate">{doc.filename}</h3>
                          <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(doc.uploaded_at)}
                            </span>
                            <span>•</span>
                            <span>{formatSize(doc.file_size)}</span>
                            {doc.stats && (
                              <>
                                <span>•</span>
                                <span>{doc.stats.words} mots</span>
                                <span>•</span>
                                <span>~{doc.stats.pages_estimate} page(s)</span>
                              </>
                            )}
                          </div>
                          
                          {/* Tags */}
                          <div className="flex flex-wrap gap-1 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {doc.filename.split('.').pop().toUpperCase()}
                            </Badge>
                            {doc.language && (
                              <Badge variant="outline" className="text-xs">
                                {doc.language === 'fr' ? '🇫🇷 FR' : doc.language === 'en' ? '🇬🇧 EN' : doc.language.toUpperCase()}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openViewModal(doc)}
                            title="Voir le contenu"
                            data-testid={`view-doc-${doc.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openAnalysisModal(doc)}
                            title="Analyser"
                            data-testid={`analyze-doc-${doc.id}`}
                          >
                            <Sparkles className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(doc.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            title="Supprimer"
                            data-testid={`delete-doc-${doc.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
        
        {/* Search Tab */}
        <TabsContent value="search" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recherche dans les documents</CardTitle>
              <CardDescription>
                Trouvez rapidement une information dans votre base documentaire
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Rechercher une information..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="search-input"
                />
                <Button onClick={handleSearch} disabled={searching || !searchQuery.trim()}>
                  {searching ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </Button>
              </div>
              
              {searchResults !== null && (
                <div className="space-y-3">
                  {searchResults.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Aucun résultat trouvé pour &quot;{searchQuery}&quot;
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">
                        {searchResults.length} résultat(s) trouvé(s)
                      </p>
                      {searchResults.map((result, idx) => (
                        <Card key={idx} className="bg-muted/30">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <h4 className="font-medium">{result.document_name}</h4>
                                <p className="text-xs text-muted-foreground">
                                  Pertinence: {Math.round(result.relevance * 100)}%
                                </p>
                              </div>
                              <Badge variant="outline">
                                {Math.round(result.relevance * 100)}%
                              </Badge>
                            </div>
                            {result.excerpts && result.excerpts.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {result.excerpts.map((excerpt, i) => (
                                  <p key={i} className="text-sm text-muted-foreground bg-background p-2 rounded">
                                    &quot;...{excerpt.slice(0, 200)}...&quot;
                                  </p>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Compare Tab */}
        <TabsContent value="compare" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Comparer des documents</CardTitle>
              <CardDescription>
                Sélectionnez 2 à 5 documents dans l&apos;onglet &quot;Documents&quot; pour les comparer
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedDocs.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedDocs.map(docId => {
                    const doc = documents.find(d => d.id === docId);
                    if (!doc) return null;
                    return (
                      <Badge key={docId} variant="secondary" className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {doc.filename}
                        <button
                          onClick={() => toggleDocSelection(docId)}
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
              
              <Button
                onClick={runComparison}
                disabled={selectedDocs.length < 2 || comparing}
                className="w-full"
                data-testid="compare-button"
              >
                {comparing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Comparaison en cours...
                  </>
                ) : (
                  <>
                    <GitCompare className="w-4 h-4 mr-2" />
                    Comparer {selectedDocs.length} document(s)
                  </>
                )}
              </Button>
              
              {compareResult && (
                <Card className="bg-muted/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      Résultat de la comparaison
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                      {compareResult}
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Analysis Modal */}
      <Dialog open={analysisModal} onOpenChange={setAnalysisModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Analyser le document
            </DialogTitle>
            <DialogDescription>
              {currentDoc?.filename}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Analysis type selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Type d&apos;analyse</label>
                <Select value={analysisType} onValueChange={setAnalysisType}>
                  <SelectTrigger data-testid="analysis-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="summary">
                      <div className="flex items-center gap-2">
                        <List className="w-4 h-4" />
                        Résumé
                      </div>
                    </SelectItem>
                    <SelectItem value="key_points">
                      <div className="flex items-center gap-2">
                        <LayoutList className="w-4 h-4" />
                        Points clés
                      </div>
                    </SelectItem>
                    <SelectItem value="question">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Question
                      </div>
                    </SelectItem>
                    <SelectItem value="structure">
                      <div className="flex items-center gap-2">
                        <FileQuestion className="w-4 h-4" />
                        Structure
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {analysisType === "summary" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Longueur</label>
                  <Select value={analysisLength} onValueChange={setAnalysisLength}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="short">Court (3-5 phrases)</SelectItem>
                      <SelectItem value="medium">Moyen (1-2 paragraphes)</SelectItem>
                      <SelectItem value="long">Détaillé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {/* Question input */}
            {analysisType === "question" && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Votre question</label>
                <Textarea
                  placeholder="Posez une question sur le contenu du document..."
                  value={analysisQuestion}
                  onChange={(e) => setAnalysisQuestion(e.target.value)}
                  rows={3}
                  data-testid="analysis-question-input"
                />
              </div>
            )}
            
            {/* Run analysis button */}
            <Button
              onClick={runAnalysis}
              disabled={analyzing || (analysisType === "question" && !analysisQuestion.trim())}
              className="w-full"
              data-testid="run-analysis-button"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Lancer l&apos;analyse
                </>
              )}
            </Button>
            
            {/* Analysis result */}
            {analysisResult && (
              <Card className="bg-muted/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    Résultat
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm">
                    {analysisResult}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* View Document Modal */}
      <Dialog open={viewModal} onOpenChange={setViewModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {viewDoc?.filename}
            </DialogTitle>
            <DialogDescription>
              {viewDoc?.stats && `${viewDoc.stats.words} mots • ~${viewDoc.stats.pages_estimate} page(s)`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-muted/30 rounded-lg p-4 max-h-[60vh] overflow-y-auto">
            <pre className="text-sm whitespace-pre-wrap font-sans">
              {viewDoc?.extracted_text || "Contenu non disponible"}
            </pre>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewModal(false)}>
              Fermer
            </Button>
            <Button onClick={() => {
              setViewModal(false);
              openAnalysisModal(viewDoc);
            }}>
              <Sparkles className="w-4 h-4 mr-2" />
              Analyser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentsPage;
